import React, { memo, useImperativeHandle,useMemo, useRef, useState } from 'react';
import {
  Box, IconButton, Menu, MenuItem, MenuProps, styled,
  Typography,
} from '@mui/material';

import {
  GallerySelectMenuMoreIcon,
  GallerySelectUploadIcon,   GallerySelectUploadListIcon,
  GallerySelectUploadListPlusIcon,
  LogoMenuIcon, LogoMenuSubitemUpdateIcon, TrashBinMenuIcon,
} from '../../Icons';
import { convertToBase64 } from '../../utils';

const ActionButton = styled(IconButton)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 32,
  height: 32,
  position: 'absolute',
  right: '8px',
  top: '16px',
  fontSize: '24px',
  backgroundColor: 'rgba(255, 255, 255, 0.38)',
  borderRadius: '50%',
  color: '#1F1F1F',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: '#FAFAFA',
  },

  ':active': {
    boxShadow: 'none',
    border: '1px solid #F3F3F3',
    backgroundColor: '#FAFAFA',
  }
});

const ActionMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    {...props}
  />
))(({theme}) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: 8,
    minWidth: 120,
    color: '#363636',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '0',
    },
    '& .MuiMenuItem-root': {
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '400',
      minHeight: '38px',

      '& .MuiSvgIcon-root': {
        fontSize: 16,
        marginRight: 8,
      },
      '&:active, &:hover': {
        backgroundColor: '#BF295C',
        color: 'white',
      },
    },
  },
}));

enum ActionType {
  update,
  delete,
}

interface FloatingImageButtonProps {
  onUpdateClick: () => void,
  onDeleteClick: () => void,
  sx?: any,
}

interface FloatingImageListMoreButtonProps {
  onClick: () => void,
  sx?: any,
}

function FloatingImageButton({
  onUpdateClick,
  onDeleteClick,
  sx,
}: FloatingImageButtonProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: ActionType) => {
    return () => {
      switch (action) {
        case ActionType.update:
          onUpdateClick?.();
          console.log('should update');
          break;
        case ActionType.delete:
          onDeleteClick?.();
          console.log('should delete');
          break;
        default:
          throw new Error('Not proper ActionType');
      }
      setAnchorEl(null);
    };
  };

  return (
    <div>
      <ActionButton
        id="action-button"
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        disableRipple
        sx={{
          ...(open ? {
            backgroundColor: open ? '#f3f3f3' : undefined,
            boxShadow: 'none',
            border: '1px solid #F3F3F3',
          } : {}),
          ...sx,
        }}
      >
        <GallerySelectMenuMoreIcon fontSize="inherit"/>
      </ActionButton>
      <ActionMenu
        id="action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'action-button',
        }}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
      >
        <MenuItem onClick={handleAction(ActionType.update)} disableRipple>
          <LogoMenuSubitemUpdateIcon fontSize="inherit"/>
          Update photo
        </MenuItem>
        <MenuItem onClick={handleAction(ActionType.delete)} disableRipple>
          <TrashBinMenuIcon/>
          Delete
        </MenuItem>
      </ActionMenu>
    </div>
  );
}

function FloatingImageListMoreButton({
  onClick,
  sx
}: FloatingImageListMoreButtonProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 32,
        height: 32,
        position: 'absolute',
        right: '8px',
        top: '16px',
        fontSize: '24px',
        backgroundColor: 'rgba(255, 255, 255, 0.38)',
        borderRadius: '50%',
        color: '#1F1F1F',
        cursor: 'pointer',
        ...sx,
      }}>
      <GallerySelectMenuMoreIcon fontSize="inherit"/>
    </Box>
  );
}

interface AddMoreImageListItemProps {
  onClick: () => void;
}

function AddMoreImageListItem({
  onClick,
}: AddMoreImageListItemProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: '6px',
        marginTop: '8px',
        height: 178,
        width: 352.5,
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '48px',
        color: '#9D9D9D',
        cursor: 'pointer',
      }}>
      <GallerySelectUploadListPlusIcon fontSize="inherit"/>
      <Typography sx={{
        color: '#080808',
        fontWeight: '400',
        fontSize: '16px',
        marginTop: '12px',
      }}>
        Add more
      </Typography>
    </Box>
  );
}

function EmptyImageListItem() {
  return (
    <Box sx={{
      display: 'flex',
      height: 178,
      width: 352.5,
      backgroundColor: 'white',
      borderRadius: '6px',
      marginTop: '8px',
      justifyContent: 'center',
      alignItems: 'center',
      color: '#9D9D9D',
      fontSize: '56px',
    }}>
      <GallerySelectUploadListIcon fontSize="inherit"/>
    </Box>
  );
}

interface GallerySelectProps {
  initialImagesArray: Array<any>,
  onChange: (newImages: Array<string>) => any;
}

const GallerySelect = memo(React.forwardRef(({
  initialImagesArray,
  onChange,
}: GallerySelectProps, ref) => {

  const [isShouldAddMore, setIsShouldAddMore] = useState<boolean>(false);
  const [imageIndexToEdit, setImageIndexToEdit] = useState<false | number>(false);
  const [imagesArray, setImagesArray] = useState<Array<any>>(initialImagesArray);

  const fileInputRef = useRef<any>(null);
  const formRef = useRef<any>(null);

  const handleRemoveImageByIndex = (index: number) => () => {
    const newImagesArray = [...imagesArray.slice(0, index), ...imagesArray.slice(index + 1)];

    setImagesArray(newImagesArray);
    onChange(newImagesArray);
  };

  const handleEditImageByIndex = (index: number) => () => {

    setImageIndexToEdit(index);

    fileInputRef?.current?.click();
  };

  const isImageWasAdded = useMemo(() => {
    return imagesArray.length !== 0;
  }, [imagesArray]);

  const handleUploadImageButton = () => {
    fileInputRef?.current?.click();
  };

  const handleUploadMoreImageButton = () => {
    setIsShouldAddMore(true);
    fileInputRef?.current?.click();
  };

  const handleDeleteImages = () => {
    formRef?.current?.reset();
    setImagesArray([]);
    onChange([]);
  };

  const handleImageChange = async (e: any) => {

    if (e.target.files) {
      const imagesToBase64Promises = [...e.target.files].map(async (file) => await convertToBase64(file));
      const newImagesArray = await Promise.all(imagesToBase64Promises);

      if (isShouldAddMore) {
        setImagesArray((state) => {
          const newState = [...state, ...newImagesArray];

          onChange(newState);
          return newState;
        });
      } else if (imageIndexToEdit !== false) {
        setImagesArray((state) => {
          const newState = [
            ...state.slice(0, imageIndexToEdit),
            ...newImagesArray,
            ...state.slice(imageIndexToEdit + 1)
          ];

          onChange(newState);
          return newState;
        });

        setImageIndexToEdit(false);
      } else {
        setImagesArray(newImagesArray);
        onChange(newImagesArray);
      }
    }

    setIsShouldAddMore(false);
  };

  useImperativeHandle(ref, () => ({
    deleteImages: handleDeleteImages
  }));

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      width: '100%',
      flex: 1,
      height: 238,
    }}>
      {
        isImageWasAdded
          ? (<Box
            sx={{
              borderRadius: '8px',
              padding: '8px',
              backgroundColor: '#F3F3F3',
            }}>
            <Box sx={{
              position: 'relative',
            }}>
              <img
                src={imagesArray[0]}
                loading="lazy"
                style={{
                  width: '100%',
                  height: 364,
                  objectFit: 'cover',
                  borderRadius: '6px',
                }}
              />
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
                top: '8px',
                left: '8px',
                borderRadius: '8px',
                height: '32px',
                backgroundColor: 'white',
                padding: '0 8px 0',
                cursor: 'default',
              }}>
                <Typography sx={{
                  fontSize: '14px',
                  fontWeight: '400',
                  color: '#363636',
                }}>
                  Cover photo
                </Typography>
              </Box>
              <FloatingImageButton
                onUpdateClick={handleEditImageByIndex(0)}
                onDeleteClick={handleRemoveImageByIndex(0)}
                sx={{
                  top: '8px',
                }}
              />
            </Box>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              width: '100%',
            }}>
              {
                imagesArray.slice(1).map((image, index) => {
                  return (
                    <Box
                      key={image.slice(0, -15)}
                      sx={{
                        display: 'flex',
                        height: 186,
                        width: 352.5,
                        position: 'relative',
                      }}>
                      <img
                        src={image}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: 178,
                          objectFit: 'cover',
                          borderRadius: '6px',
                          marginTop: '8px',
                        }}
                      />
                      <FloatingImageButton
                        onUpdateClick={handleEditImageByIndex(index + 1)}
                        onDeleteClick={handleRemoveImageByIndex(index + 1)}
                      />
                    </Box>
                  );
                })
              }
              {
                imagesArray.length % 2 !== 0 ? (<EmptyImageListItem/>) : null
              }
              <AddMoreImageListItem onClick={handleUploadMoreImageButton}/>
            </Box>
          </Box>)
          : (<Box
            onClick={handleUploadImageButton}
            sx={{
              padding: 0,
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px #828282 dashed',
              cursor: 'pointer',
              boxShadow: 'none',
              width: '100%',
              height: 380,
            }}
          >
            <Box style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              color: '#9D9D9D',
              fontSize: '100px',
            }}>
              <GallerySelectUploadIcon fontSize="inherit"/>
              <Typography sx={{
                color: '#BF295C',
                fontWeight: '600',
                fontSize: '24px',
                marginTop: '18px',
                marginBottom: '4px',
              }}>
                Upload photos
              </Typography>
              <Typography sx={{
                color: '#080808',
                fontWeight: '400',
                fontSize: '16px',
              }}>
                Choose at least 5 photos
              </Typography>
              <Typography sx={{
                color: '#BF295C',
                fontWeight: '500',
                fontSize: '16px',
                marginTop: '20px',
                textDecoration: 'underline',
                textUnderlineOffset: '4px',
              }}>
                Upload from your device
              </Typography>
            </Box>
          </Box>)
      }
      <form ref={formRef}>
        <input
          ref={fileInputRef}
          onChange={handleImageChange}
          style={{
            display: 'none'
          }}
          type="file"
          accept="image/*"
          multiple
        />
      </form>
    </Box>
  );
}));

export {GallerySelect}
